/**
 * Unified storage utility for sol-frontend
 * Handles localStorage with expiry, auth tokens, and session data
 */

// Storage keys
const STORAGE_KEYS = {
  AUTH_DATA: 'sol_auth_data',
  ZOOM_SESSION: 'sol_zoom_session',
  TEMPLATES: 'sol_templates_cache'
};

/**
 * Core localStorage functions with expiry support
 */

// Get data from localStorage with expiry check
export const getWithExpiry = (key) => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;

  try {
    const item = JSON.parse(itemStr);
    // Check if expired
    if (item.expiry && Date.now() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value !== undefined ? item.value : item;
  } catch (error) {
    console.error('Error parsing localStorage item:', error);
    return null;
  }
};

// Set data to localStorage with optional expiry (TTL in milliseconds)
export const setWithExpiry = (key, value, ttl = null) => {
  const item = {
    value,
    expiry: ttl ? Date.now() + ttl : null
  };
  localStorage.setItem(key, JSON.stringify(item));
};

// Remove item from localStorage
export const remove = (key) => {
  localStorage.removeItem(key);
};

// Check if item exists and is not expired
export const hasValid = (key) => {
  return getWithExpiry(key) !== null;
};

/**
 * JWT utilities
 */

export const decodeJwt = (token) => {
  try {
    if (!token || typeof token !== 'string') return null;
    
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

export const isTokenExpired = (decodedToken) => {
  if (!decodedToken || !decodedToken.exp) return true;
  const currentTime = Math.floor(Date.now() / 1000);
  return decodedToken.exp < currentTime;
};

/**
 * Auth data management
 */

export const saveAuthData = (authData, tokenInfo) => {
  try {
    let expiryTime = null;
    
    // Calculate TTL from token's expires_at timestamp if available
    if (tokenInfo && tokenInfo.expires_at) {
      const tokenExpiry = new Date(tokenInfo.expires_at);
      const ttl = tokenExpiry.getTime() - Date.now();
      
      // Only save if token hasn't expired yet
      if (ttl > 0) {
        expiryTime = ttl;
      } else {
        console.warn('Token is already expired, not saving');
        return;
      }
    }
    
    setWithExpiry(STORAGE_KEYS.AUTH_DATA, authData, expiryTime);
  } catch (error) {
    console.error('Error saving auth data:', error);
  }
};

export const getAuthData = () => {
  return getWithExpiry(STORAGE_KEYS.AUTH_DATA);
};

export const clearAuthData = () => {
  remove(STORAGE_KEYS.AUTH_DATA);
};

export const isAuthValid = () => {
  const authData = getAuthData();
  if (!authData) return false;
  
  const decoded = authData.decoded_jwt;
  return decoded ? !isTokenExpired(decoded) : false;
};

/**
 * Zoom session management
 */

export const saveZoomSession = (sessionData, ttl = 2 * 60 * 60 * 1000) => { // 2 hours default
  setWithExpiry(STORAGE_KEYS.ZOOM_SESSION, sessionData, ttl);
};

export const getZoomSession = () => {
  return getWithExpiry(STORAGE_KEYS.ZOOM_SESSION);
};

export const clearZoomSession = () => {
  remove(STORAGE_KEYS.ZOOM_SESSION);
};

/**
 * Templates cache management
 */

export const saveTemplatesCache = (templatesData, ttl = 5 * 60 * 1000) => { // 5 minutes default
  setWithExpiry(STORAGE_KEYS.TEMPLATES, templatesData, ttl);
};

export const getTemplatesCache = () => {
  return getWithExpiry(STORAGE_KEYS.TEMPLATES);
};

export const clearTemplatesCache = () => {
  remove(STORAGE_KEYS.TEMPLATES);
};

/**
 * Evaluation history management
 */

export const saveEvaluationHistory = (evaluation, ttl = 24 * 60 * 60 * 1000) => { // 24 hours default
  const key = 'sol_evaluation_history';
  const existing = getWithExpiry(key) || [];
  
  // Add new evaluation to beginning of array
  const updated = [
    {
      ...evaluation,
      timestamp: new Date().toISOString(),
      id: evaluation.evaluation_id || Date.now().toString()
    },
    ...existing
  ].slice(0, 10); // Keep only last 10 evaluations
  
  setWithExpiry(key, updated, ttl);
  return updated;
};

export const getEvaluationHistory = () => {
  const key = 'sol_evaluation_history';
  return getWithExpiry(key) || [];
};

export const deleteEvaluationHistoryItem = (id) => {
  const key = 'sol_evaluation_history';
  const existing = getWithExpiry(key) || [];
  const filtered = existing.filter(item => item.id !== id);
  setWithExpiry(key, filtered, 24 * 60 * 60 * 1000); // Reset to 24 hours
  return filtered;
};

export const clearEvaluationHistory = () => {
  const key = 'sol_evaluation_history';
  remove(key);
};

/**
 * Case status management
 */

export const saveCaseStatus = (caseId, statusData, ttl = 24 * 60 * 60 * 1000) => { // 24 hours default
  const key = `sol_case_status_${caseId}`;
  setWithExpiry(key, statusData, ttl);
};

export const getCaseStatus = (caseId) => {
  const key = `sol_case_status_${caseId}`;
  return getWithExpiry(key);
};

export const clearCaseStatus = (caseId) => {
  const key = `sol_case_status_${caseId}`;
  remove(key);
};

export const updateCaseProgress = (caseId, progressData) => {
  const existingStatus = getCaseStatus(caseId) || {};
  const updatedStatus = {
    ...existingStatus,
    ...progressData,
    last_updated: new Date().toISOString()
  };
  saveCaseStatus(caseId, updatedStatus);
  return updatedStatus;
};

/**
 * Session tracking for provider activities
 */

export const saveSessionActivity = (caseId, activityData, ttl = 12 * 60 * 60 * 1000) => { // 12 hours default
  const key = `sol_session_activity_${caseId}`;
  const existingActivity = getWithExpiry(key) || {};
  const updatedActivity = {
    ...existingActivity,
    ...activityData,
    last_activity: new Date().toISOString()
  };
  setWithExpiry(key, updatedActivity, ttl);
  return updatedActivity;
};

export const getSessionActivity = (caseId) => {
  const key = `sol_session_activity_${caseId}`;
  return getWithExpiry(key);
};

export const clearSessionActivity = (caseId) => {
  const key = `sol_session_activity_${caseId}`;
  remove(key);
};

/**
 * Clear all sol-frontend storage
 */
export const clearAllStorage = () => {
  Object.values(STORAGE_KEYS).forEach(key => remove(key));
  
  // Clear case-specific data
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('sol_case_status_') || key.startsWith('sol_session_activity_')) {
      remove(key);
    }
  });
};

// Legacy compatibility exports
export const getLocalStorageWithExpiry = getWithExpiry;
export const setLocalStorageWithExpiry = setWithExpiry;
export const removeLocalStorageItem = remove;
export const hasValidLocalStorageItem = hasValid;